import { useEffect, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Clock, BadgeCheck, Waves } from "lucide-react";
import VideoGuide from "@/components/enea/VideoGuide";
import { toast } from "sonner";
import ValuationFlow from "@/components/enea/ValuationFlow";
import Footer from "@/components/enea/Footer";

export default function Valutazione() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const beta = searchParams.get("beta") === "1";

  const initialName = useMemo(() => {
    const fromState = location.state?.ownerName;
    if (fromState) return fromState;
    try { return sessionStorage.getItem("hec_owner_name") || ""; } catch (e) { return ""; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = "Valutazione immobiliare gratuita — Home Exchange Copilot AI";
    window.scrollTo(0, 0);
    if (searchParams.get("pagamento") === "annullato") {
      toast("Pagamento annullato — i tuoi dati sono al sicuro, puoi riprendere quando vuoi.", { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F0F9FC]" data-testid="valutazione-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0b2545] text-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="valutazione-brand">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#A9D6E5] to-[#14B8A6] flex items-center justify-center">
              <Waves className="w-4 h-4 text-[#0b2545]" />
            </span>
            <span className="text-sm font-semibold tracking-tight leading-tight">
              Home Exchange Copilot AI
              <span className="block text-[10px] font-normal text-[#A9D6E5]">Gruppo Vela · Valutazioni in tutta Italia</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#A9D6E5] hover:text-white transition" data-testid="valutazione-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Torna alla home
          </Link>
        </div>
      </header>

      {beta && (
        <div className="bg-amber-100 border-b border-amber-200 text-amber-800 text-xs text-center py-2 px-4" data-testid="valutazione-beta-banner">
          🧪 Versione Beta — il tuo feedback è prezioso
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#A9D6E5]/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-[360px] h-[360px] rounded-full bg-[#14B8A6]/10 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6 text-center">
          <div className="enea-eyebrow inline-flex items-center gap-2 justify-center mb-4 text-[#14B8A6]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">Valutazione immobiliare · MCM</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-semibold leading-tight text-[#0b2545]">
            Diagnostica
            <span className="block mt-1 text-[#14B8A6]">dell'Immobile.</span>
          </h1>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base text-slate-600 leading-relaxed">
            Compila la scheda in ~2 minuti: ricevi il <b>Report Diagnostico Enea</b> con la
            forbice di vendibilità reale (quotazioni OMI ufficiali min–max) e la
            pre-valutazione di idoneità al protocollo <b>“Vendita Zero Vincoli”</b>.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5" data-testid="valutazione-trust-chips">
            {[
              { icon: BadgeCheck, t: "Gratuita e senza impegno" },
              { icon: Clock, t: "~2 minuti" },
              { icon: ShieldCheck, t: "Quotazioni OMI · Agenzia delle Entrate" },
            ].map(({ icon: Icon, t }) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0b2545] bg-white border border-slate-200 rounded-full px-3 py-1.5 shadow-sm">
                <Icon className="w-3.5 h-3.5 text-[#14B8A6]" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Video guida */}
      <section className="relative max-w-3xl mx-auto px-4 sm:px-6 pb-8" data-testid="valutazione-video-guide">
        <VideoGuide page="valutazione" />
      </section>

      {/* Flow */}
      <section className="relative max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <ValuationFlow beta={beta} initialName={initialName} initialPremium={searchParams.get("premium") === "1"} />
        <p className="mt-4 text-center text-[11px] text-slate-500">
          Arrivi da un QR code? Sei nel posto giusto: compila la scheda e ricevi subito la tua valutazione.
        </p>
      </section>

      <Footer />
    </div>
  );
}
